import * as React from 'react';
import { Context } from './Context';

interface Props {
	children: React.ReactNode;
	contextKey?: string;
	handler?: (<Error>(error: Error, errorInfo?: React.ErrorInfo) => React.ReactNode) | null;
	fallback?(): React.ReactNode;
}

type State =
	| { contextKey: string | undefined; errorMessage: null; failed: true }
	| { contextKey: string | undefined; errorMessage: React.ReactNode; failed: false }
	| { contextKey: string | undefined; errorMessage: null; failed: null };

export class ErrorBoundary extends React.PureComponent<Props, State> {
	public static contextType = Context;

	public static getDerivedStateFromError(): Partial<State> | null {
		return { errorMessage: null, failed: true };
	}

	public static getDerivedStateFromProps(nextProps: Readonly<Props>, prevState: State): Partial<State> | null {
		return nextProps.contextKey !== prevState.contextKey
			? {
					contextKey: nextProps.contextKey,
					errorMessage: null,
					failed: null,
			  }
			: null;
	}

	// eslint-disable-next-line react/static-property-placement
	public declare context: React.ContextType<typeof Context>;

	public constructor(props: Props) {
		super(props);
		this.state = { contextKey: props.contextKey, errorMessage: null, failed: null };
	}

	public componentDidCatch(error: globalThis.Error, errorInfo: React.ErrorInfo) {
		(this.props.handler ?? this.context.handler)?.(error, errorInfo);
	}

	public render(): React.ReactNode {
		const fallback = this.props.fallback ?? this.context.fallback;

		if (this.state.failed === true) {
			return fallback?.();
		}

		const handler = this.props.handler ?? this.context.handler;
		const updateErrorMessage = (errorMessage: React.ReactNode) => {
			this.setState({ contextKey: this.props.contextKey, errorMessage, failed: false });
		};
		const errorOccurred = (error: unknown) => {
			updateErrorMessage(error !== null ? handler?.(error) : null);
		};

		const value = {
			errorMessage: this.state.errorMessage,
			fallback,
			handler,
			onError: errorOccurred,
			onUserError: updateErrorMessage,
		};
		return <Context.Provider value={value}>{this.props.children}</Context.Provider>;
	}
}
